import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tab, Tabs } from 'react-bootstrap';
import { apiClient } from '../api';
import { integrationIntegration } from './connection-points/IntegrationsStep';
import OverviewTab from './EditMpxTenantPage/OverviewTab';
import ConfigurationTab from './EditMpxTenantPage/ConfigurationTab';
import MpxTenantIntegrationsTab from './EditMpxTenantPage/MpxTenantIntegrationsTab';
import { DeleteButton } from './shared/DeleteButton';

interface MpxTenantForm {
  name: string;
  description: string;
  did: string;
  api_endpoint: string;
  our_did: string;
  device_token: string;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;

  integrations?: integrationIntegration[];
}

const EditMpxTenantPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = id !== undefined && id !== 'new';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<MpxTenantForm>({
    name: '',
    description: '',
    did: '',
    api_endpoint: '',
    our_did: '',
    device_token: '',
    enabled: true,
    integrations: [],
  });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [hasIntegrationErrors, setHasIntegrationErrors] = useState(false);
  const [integrationsModified, setIntegrationsModified] = useState(false);
  const [savingIntegrations, setSavingIntegrations] = useState(false);
  const [formModified, setFormModified] = useState(false);
  const [originalForm, setOriginalForm] = useState<MpxTenantForm | null>(null);

  useEffect(() => {
    if (isEditMode && id) {
      fetchTenant();
    }
  }, [id, isEditMode]);

  // Add keyboard shortcut for save (Cmd+S or Ctrl+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (activeTab === 'integrations' && integrationsModified) {
          handleSaveIntegrations();
        } else if ((activeTab === 'overview' || activeTab === 'configuration') && formModified) {
          handleSubmit();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, integrationsModified, formModified]);

  // Track form changes
  useEffect(() => {
    if (!originalForm) return;
    const hasChanges = 
      form.name !== originalForm.name ||
      form.description !== originalForm.description ||
      form.enabled !== originalForm.enabled;
    setFormModified(hasChanges);
  }, [form.name, form.description, form.enabled, originalForm]);

  const fetchTenant = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/mpx-tenants/${id}`);
      const loadedForm = {
        name: response.data.name,
        description: response.data.description,
        did: response.data.did || '',
        api_endpoint: response.data.api_endpoint,
        our_did: response.data.our_did || '',
        device_token: response.data.device_token || '',
        enabled: response.data.enabled !== undefined ? response.data.enabled : true,
        created_at: response.data.created_at,
        updated_at: response.data.updated_at,
        integrations: [],
      };
      setForm(loadedForm);
      setOriginalForm(loadedForm);
      setFormModified(false);

      // Load integrations from separate endpoint
      try {
        await fetchTenantIntegrations();
      } catch (err) {
        console.error('Error in fetchTenantIntegrations call:', err);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load MPX tenant');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantIntegrations = async () => {
    if (!id) return;
    try {
      const response = await apiClient.get(`/mpx-tenants/${id}/integrations`);
      setForm(prev => ({
        ...prev,
        integrations: response.data.integration_integrations || []
      }));
      setIntegrationsModified(false);
    } catch (error: any) {
      console.error('Failed to load MPX tenant integrations:', error);
      // Don't show error to user, just keep empty integrations
    }
  };

  const handleIntegrationsChange = (integrations: integrationIntegration[]) => {
    setForm({ ...form, integrations });
    setIntegrationsModified(true);
  };

  const handleSaveIntegrations = async () => {
    if (!id) return;

    try {
      setSavingIntegrations(true);
      await apiClient.put(`/mpx-tenants/${id}/integrations`, {
        integration_integrations: form.integrations
      });
      setSuccess('Integrations saved successfully!');
      setIntegrationsModified(false);
      setTimeout(() => setSuccess(null), 3000);
      await fetchTenantIntegrations(); // Refresh
    } catch (error: any) {
      setError(error.message || 'Failed to save integrations');
    } finally {
      setSavingIntegrations(false);
    }
  };

  const handleSubmit = async () => {
    if (!id) return;

    try {
      setSaving(true);
      setError('');
      await apiClient.put(`/mpx-tenants/${id}`, {
        name: form.name,
        description: form.description,
        enabled: form.enabled,
      });
      // Update original form to match saved values
      setOriginalForm(prev => prev ? {
        ...prev,
        name: form.name,
        description: form.description,
        enabled: form.enabled
      } : null);
      // Explicitly set formModified to false since we just saved
      setFormModified(false);
      setSuccess('MPX tenant updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to update MPX tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleDidChange = async (newDid: string) => {
    if (!id) return;
    
    // Don't make API call if DID hasn't changed
    if (newDid === form.did) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      // When DID changes, the backend will trigger re-authentication
      await apiClient.put(`/mpx-tenants/${id}`, {
        did: newDid,
      });
      setSuccess('DID updated and re-authenticated successfully!');
      setTimeout(() => setSuccess(null), 3000);
      await fetchTenant(); // Refresh to get new our_did and api_endpoint
    } catch (error: any) {
      setError(error.message || 'Failed to update DID');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      setSaving(true);
      await apiClient.delete(`/mpx-tenants/${id}`);
      navigate('/integrations', {
        state: {
          success: `MPX Tenant "${form.name}" deleted successfully`
        }
      });
    } catch (error: any) {
      setError(error.message || 'Failed to delete MPX tenant');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !form.name) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/integrations')}>
          <i className="fas fa-arrow-left me-2"></i>
          Back to Integrations
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card shadow mb-4">
        <div className="card-header py-3 d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-primary">
            <i className="fas fa-cloud me-2"></i> Edit MPX Tenant: {form.name}
          </h6>
          <div>
            {isEditMode && (
              <button 
                className={`btn btn-sm btn-primary mr-2 ${saving || !formModified ? 'disabled' : ''}`}
                onClick={handleSubmit}
                disabled={saving || !formModified}
              >
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i> 
                {saving ? ' Saving...' : ' Save'}
              </button>
            )}
            {isEditMode && (
              <DeleteButton
                onDelete={handleDelete}
                className="mr-2"
                title="Delete this MPX tenant"
                disabled={saving}
                variant="danger"
              >
                Delete
              </DeleteButton>
            )}
            <button className="btn btn-sm btn-secondary" onClick={() => navigate('/integrations')}>
              <i className="fas fa-times"></i> Close
            </button>
          </div>
        </div>
        <div className="card-body-channel-editor">
          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
              <button type="button" className="close" onClick={() => setError('')}>
                <span>&times;</span>
              </button>
            </div>
          )}
          {success && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <i className="fas fa-check-circle me-2"></i>
              {success}
              <button type="button" className="close" onClick={() => setSuccess(null)}>
                <span>&times;</span>
              </button>
            </div>
          )}

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || 'overview')}
            className="mb-3 custom-channel-tabs"
          >
            {/* Overview Tab */}
            <Tab eventKey="overview" title={<><i className="fas fa-info-circle"></i> Overview</>}>
              <OverviewTab
                form={form}
                setForm={setForm}
                isEditMode={isEditMode}
                saving={saving}
                id={id}
                handleSubmit={handleSubmit}
                handleDelete={handleDelete}
                onNavigate={() => navigate('/integrations')}
              />
            </Tab>

            {/* Configuration Tab - Only in edit mode */}
            {isEditMode && (
              <Tab eventKey="configuration" title={<><i className="fas fa-cog"></i> Configuration</>}>
                <ConfigurationTab
                  tenantId={id || ''}
                  did={form.did}
                  apiEndpoint={form.api_endpoint}
                  ourDid={form.our_did}
                  onDidChange={handleDidChange}
                />
              </Tab>
            )}

            {/* Integrations Tab - Only in edit mode */}
            {isEditMode && (
              <Tab eventKey="integrations" title={<><i className="fas fa-plug"></i> Integrations</>}>
                <MpxTenantIntegrationsTab
                  integrations={form.integrations || []}
                  onIntegrationsChange={handleIntegrationsChange}
                  onValidationChange={setHasIntegrationErrors}
                  onSave={handleSaveIntegrations}
                  isModified={integrationsModified}
                  isSaving={savingIntegrations}
                />
              </Tab>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default EditMpxTenantPage;
